import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { fmtDT } from "../lib/constants";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle, XCircle, WarningCircle, CaretDown, CaretUp, Lock,
} from "@phosphor-icons/react";

const GATE_LABELS = {
  PICKUP_EVIDENCE_CLEARANCE: "Pickup Evidence Clearance",
  DESTINATION_DISPATCH_CLEARANCE: "Destination Dispatch Clearance",
};

const STATUS_STYLES = {
  PENDING: "bg-[hsl(var(--muted)/0.15)] text-[hsl(var(--muted))]",
  APPROVED: "bg-emerald-500/15 text-emerald-600",
  REJECTED: "bg-red-500/15 text-red-600",
  OVERRIDDEN: "bg-amber-500/15 text-amber-600",
};

function GateCard({ shipmentId, gate, canDecide, canOverride, reload }) {
  const [open, setOpen] = useState(gate.status === "PENDING" || gate.status === "REJECTED");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'reject' | 'override'

  const act = async (action, body) => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${shipmentId}/greenlights/${gate.gate_code}/${action}`, body || {});
      toast.success(`${GATE_LABELS[gate.gate_code]} ${action}d`);
      setReason("");
      setMode(null);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || e.displayMessage || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const submitReasoned = (action) => {
    if (reason.trim().length < 6) { toast.error("Reason required (min 6 chars)"); return; }
    act(action, { reason: reason.trim() });
  };

  // M9B U3 — Pickup Evidence is the sole retained manual gate: Approve-only (no Reject/Override).
  const approveOnly = gate.gate_code === "PICKUP_EVIDENCE_CLEARANCE";

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`greenlight-card-${gate.gate_code}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-[hsl(var(--bg-subtle))]"
        data-testid={`greenlight-toggle-${gate.gate_code}`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          {gate.all_met ? <CheckCircle size={15} className="text-emerald-600" />
            : <WarningCircle size={15} className="text-amber-600" />}
          {GATE_LABELS[gate.gate_code]}
        </span>
        <span className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${STATUS_STYLES[gate.status]}`}
            data-testid={`greenlight-status-${gate.gate_code}`}>
            {gate.status}
          </span>
          {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="p-3 space-y-3">
          <ul className="space-y-1">
            {gate.requirements.map((r) => (
              <li key={r.key} className="flex items-start gap-2 text-xs" data-testid={`greenlight-req-${gate.gate_code}-${r.key}`}>
                {r.met
                  ? <CheckCircle size={13} className="text-emerald-600 mt-0.5 shrink-0" />
                  : <XCircle size={13} className={`${r.optional ? "text-[hsl(var(--muted))]" : "text-red-500"} mt-0.5 shrink-0`} />}
                <span className={r.met ? "" : "text-[hsl(var(--muted))]"}>
                  {r.label}{r.optional ? " (optional)" : ""}
                  {r.detail ? <span className="mono text-[hsl(var(--muted))]"> · {r.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>

          {gate.status !== "PENDING" && (
            <div className="text-[11px] text-[hsl(var(--muted))] border-t pt-2">
              {gate.status} by <span className="mono">{gate.approver_role}</span>
              {gate.decided_at ? ` · ${fmtDT(gate.decided_at)}` : ""}
              {gate.reason ? <div className="italic mt-0.5">“{gate.reason}”</div> : null}
            </div>
          )}

          {!gate.enforced && (
            <div className="text-[10px] text-[hsl(var(--muted))] flex items-center gap-1">
              <Lock size={11} /> Pre-cutoff shipment — greenlight not enforced.
            </div>
          )}

          {/* Decision controls */}
          {(canDecide || canOverride) && (
            <div className="border-t pt-2 space-y-2">
              {mode === null && (
                <div className="flex flex-wrap gap-2">
                  {canDecide && (
                    <button
                      className="btn-primary text-[11px] h-7 px-2 disabled:opacity-40"
                      disabled={busy || !gate.all_met || gate.status === "APPROVED"}
                      onClick={() => act("approve")}
                      data-testid={`greenlight-approve-${gate.gate_code}`}
                    >
                      Approve
                    </button>
                  )}
                  {canDecide && !approveOnly && (
                    <button
                      className="btn-ghost text-[11px] h-7 px-2 border border-red-400 text-red-600"
                      disabled={busy}
                      onClick={() => setMode("reject")}
                      data-testid={`greenlight-reject-${gate.gate_code}`}
                    >
                      Reject
                    </button>
                  )}
                  {canOverride && !approveOnly && !gate.all_met && (
                    <button
                      className="btn-ghost text-[11px] h-7 px-2 border border-amber-400 text-amber-600"
                      disabled={busy}
                      onClick={() => setMode("override")}
                      data-testid={`greenlight-override-${gate.gate_code}`}
                    >
                      Override (missing evidence)
                    </button>
                  )}
                  {canDecide && !approveOnly && !gate.all_met && !canOverride && (
                    <span className="text-[10px] text-[hsl(var(--muted))] self-center">
                      Evidence incomplete — only SUPER_ADMIN can override.
                    </span>
                  )}
                  {approveOnly && !gate.all_met && (
                    <span className="text-[10px] text-[hsl(var(--muted))] self-center">
                      Pickup evidence incomplete — approve becomes available once all requirements are met.
                    </span>
                  )}
                </div>
              )}

              {mode && (
                <div className="space-y-2">
                  <textarea
                    className="input-field text-xs w-full"
                    rows={2}
                    placeholder={mode === "reject" ? "Reason for rejection (min 6 chars)…" : "Reason for override (min 6 chars)…"}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid={`greenlight-reason-${gate.gate_code}`}
                  />
                  <div className="flex gap-2">
                    <button
                      className="btn-primary text-[11px] h-7 px-2"
                      disabled={busy}
                      onClick={() => submitReasoned(mode)}
                      data-testid={`greenlight-submit-${mode}-${gate.gate_code}`}
                    >
                      Confirm {mode}
                    </button>
                    <button className="btn-ghost text-[11px] h-7 px-2" disabled={busy}
                      onClick={() => { setMode(null); setReason(""); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GreenlightPanel({ shipmentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/shipments/${shipmentId}/greenlights`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [shipmentId]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="grid-card" data-testid="greenlight-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} weight="bold" />
          <h3 className="heading text-sm">CONTROL TOWER GREENLIGHTS</h3>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {loading && <p className="text-xs text-[hsl(var(--muted))]">Loading greenlights…</p>}
        {!loading && !data && <p className="text-xs text-[hsl(var(--muted))]">Greenlights unavailable.</p>}
        {!loading && data && data.gates.map((g) => (
          <GateCard
            key={g.gate_code}
            shipmentId={shipmentId}
            gate={g}
            canDecide={g.can_decide ?? data.can_decide}
            canOverride={data.can_override}
            reload={load}
          />
        ))}
      </div>
    </section>
  );
}
